'use client';

import React from 'react';
import Button from '@/ui/components/shared/Button';
import { urlParameterCodes } from '@/constants/urlParams';
import { usePathname } from 'next/navigation';
import { useShallowRouter } from '@/ui/hooks/useShallowRouter';
import Link from 'next/link';
import { useSubnavData } from '@/ui/providers/subnavDataProvider';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { ShapeContent } from '@/utils/crystallize-shape-helper';
import { ButtonVariants } from '../shared/Button/types/ButtonVariants';
import { parseSubnavData } from './utils';

export default function SubNav() {
  const { subnavData } = useSubnavData();

  const currentPath = usePathname();
  const shallowRouter = useShallowRouter();
  const { localizedLink } = useLocaleLink();

  // Ignore legacy shapes
  if (subnavData?.shape?.identifier !== 'subnav-v2') {
    return undefined;
  }

  const {
    title,
    'cta-items': ctaItems,
    'subnav-items': subnavItems,
  } = parseSubnavData(subnavData as ShapeContent);

  const cleanedSubNavItems = subnavItems?.filter((item) => !item['hide-on-desktop']) || [];
  const cleanedCtaItems = ctaItems || [];

  const openChooser = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();

    shallowRouter.push(currentPath, { [urlParameterCodes.productDetail.chooserOpen]: true }, true);
  };

  return (
    subnavData && (
      <div className="z-30 mx-auto mt-2 hidden max-w-screen-2xl rounded-md bg-white shadow-nuki-blured lg:block">
        <div className="flex items-center justify-between">
          <div className="flex w-full justify-between gap-5 xl:grid xl:grid-cols-3">
            {/* Title */}
            <div className="inline-flex items-center px-4 py-2 md:px-5">
              {title && <h3 className="hidden text-lg font-bold md:block">{title}</h3>}
            </div>

            {/* Subitems container */}
            <div className="flex justify-center self-stretch">
              <ul className="flex h-full items-stretch gap-2">
                {cleanedSubNavItems.map((item, index) => {
                  const rawUrl = item?.['internal-path']?.[0]?.path || item?.['link-url'];
                  const url = rawUrl ? localizedLink(rawUrl) : undefined;

                  return (
                    <li key={`nav-item-${index}`}>
                      <Link
                        href={url || '#'}
                        target={item?.['open-in-new-tab'] ? '_blank' : '_self'}
                        className="inline-flex h-full w-full items-center border-b-4 border-primary-5 px-2 font-semibold duration-300 hover:border-primary-100"
                        onClick={(e) => {
                          if (item['open-chooser'] && !url) {
                            openChooser(e as React.MouseEvent<HTMLAnchorElement>);
                          }
                        }}
                      >
                        <span className="-mb-1 inline-block text-center leading-tight">
                          {item?.text}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Buttons container */}
            <div className="flex justify-end px-4 py-2 md:px-5">
              {cleanedCtaItems.length > 0 && (
                <div className="flex w-full justify-between gap-3 md:w-fit md:justify-start">
                  {cleanedCtaItems?.map((cta, index) => {
                    const url = cta?.['internal-path']?.[0]?.path || cta?.['link-url'];

                    return (
                      <Button
                        key={index}
                        type="button"
                        href={url}
                        openInNewTab={cta['open-in-new-tab']}
                        variant={(cta?.variant?.[0] as ButtonVariants) || 'primary'}
                        className="text-sm font-bold md:rounded-xs md:py-2 md:normal-case"
                        onClick={(e) => {
                          if (cta['open-chooser'] && !url) {
                            openChooser(e as React.MouseEvent<HTMLButtonElement>);
                          }
                        }}
                      >
                        {cta?.text}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
}
